package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.asMoney
import com.routexk.quiz.funnel.ui.DeductionCard
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.funnel.ui.StatusBadge
import com.routexk.quiz.funnel.ui.StickyCtaBar
import com.varabyte.kobweb.compose.ui.styleModifier
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.theme.toRgbString
import com.routexk.quiz.ui.utils.isMobile
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/results")
@Composable
fun ResultsPage() {
    val ctx = rememberPageContext()
    val mobile = isMobile()
    val answers = remember { FunnelStore.answers }
    val estimate = remember { FunnelStore.estimate }

    LaunchedEffect(Unit) {
        if (!answers.quizComplete) ctx.router.navigateTo("/quiz")
        else {
            Ga4.screenView("results", 4, "estimated_deduction" to estimate.taxDeductionUsd)
            FbPixel.pageView()
            FbPixel.track(
                "ViewContent",
                params = arrayOf(
                    "content_name" to "results",
                    "value" to estimate.taxDeductionUsd,
                    "currency" to "USD",
                ),
            )
        }
    }
    if (!answers.quizComplete) return

    FunnelLayout(
        title = com.routexk.quiz.i18n.t("RouteXK — Your Tax Savings", "RouteXK — Tu ahorro de impuestos"),
        warm = true,
        maxContentWidth = 1000.rpx,
        // Design puts the warm ball left of center and higher up on desktop
        patternLeftPercent = if (mobile) 50 else 30,
        patternTop = if (mobile) 120.rpx else (-80).rpx
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .margin(top = 24.rpx)
                .flexWrap(FlexWrap.Wrap)
                .gap(if (mobile) 24.rpx else 40.rpx),
            horizontalArrangement = Arrangement.Center
        ) {
            Column(
                Modifier.maxWidth(440.rpx).gap(if (mobile) 16.rpx else 20.rpx),
                horizontalAlignment = Alignment.Start
            ) {
                StatusBadge(FunnelCopy.RESULTS_BADGE, prefix = "")

                H1(
                    attrs = Modifier
                        .margin(0.rpx)
                        .fontSize(if (mobile) 32.rpx else 40.rpx)
                        .fontWeight(FontWeight.Bold)
                        .lineHeight(1.2)
                        .fontFamily(AppTypography.SECONDARY_FONT_NAME, AppTypography.SECONDARY_FONT_FALLBACK)
                        .toAttrs()
                ) {
                    Text(FunnelCopy.RESULTS_TITLE_PART1)
                    Span({ style { color(Color(AppColors.Primary.toRgbString())) } }) {
                        Text(FunnelCopy.RESULTS_TITLE_ACCENT)
                    }
                    Text(FunnelCopy.RESULTS_TITLE_PART2)
                }

                DeductionCard(
                    title = FunnelCopy.RESULTS_CARD_TITLE,
                    amountDigits = estimate.taxDeductionUsd.asMoney().removePrefix("$"),
                    pill = FunnelCopy.RESULTS_CARD_PILL
                )

                SpanText(
                    FunnelCopy.RESULTS_CAPTION,
                    Modifier.fontSize(14.rpx).lineHeight(20.rpx)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x67, 0x67, 0x7D))
                        .padding(leftRight = 16.rpx)
                )
            }

            Column(Modifier.maxWidth(440.rpx).fillMaxWidth().gap(if (mobile) 8.rpx else 14.rpx)) {
                if (!mobile) {
                    SpanText(
                        FunnelCopy.RESULTS_FOUND_TITLE,
                        Modifier
                            .fontSize(14.rpx)
                            .lineHeight(16.rpx)
                            .fontWeight(FontWeight.Medium)
                            .letterSpacing(0.5.rpx)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x67, 0x67, 0x7D))
                    )
                }

                listOf(
                    FunnelCopy.RESULTS_FOUND_1 to false,
                    FunnelCopy.RESULTS_FOUND_2 to false,
                    FunnelCopy.RESULTS_FOUND_3 to true
                ).forEach { (finding, highlight) ->
                    Box(
                        Modifier
                            .fillMaxWidth()
                            .backgroundColor(
                                if (highlight) com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF6, 0xFB, 0xFF)
                                else com.varabyte.kobweb.compose.ui.graphics.Colors.White
                            )
                            .border(
                                1.rpx, LineStyle.Solid,
                                if (highlight) com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xA7, 0xBD, 0xFE)
                                else com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE6, 0xE6, 0xF3)
                            )
                            .borderRadius(20.rpx)
                            .padding(if (mobile) 16.rpx else 24.rpx)
                            .styleModifier { property("box-shadow", AppColors.Funnel.CardShadow) }
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(if (mobile) 8.rpx else 16.rpx)) {
                            if (highlight) {
                                SpanText("🔮", Modifier.fontSize(28.rpx).flexShrink(0))
                            } else {
                                org.jetbrains.compose.web.dom.Img(
                                    src = "/funnel/check-icon.svg",
                                    attrs = { attr("aria-hidden", "true"); style { width(20.rpx); height(15.rpx); flexShrink(0) } }
                                )
                            }
                            SpanText(
                                finding,
                                Modifier
                                    .fontSize(if (mobile) 15.rpx else 18.rpx)
                                    .lineHeight(if (mobile) 22.rpx else 24.rpx)
                                    .fontWeight(if (highlight) FontWeight.Medium else FontWeight.Normal)
                                    .color(AppColors.Funnel.TextDark)
                            )
                        }
                    }
                }

                val onClaim = {
                    Ga4.event("claim_now_clicked", "estimated_deduction" to estimate.taxDeductionUsd)
                    FbPixel.trackCustom("ClaimClicked")
                    ctx.router.navigateTo("/email")
                }
                if (mobile) {
                    // Pinned to the viewport bottom so the CTA is reachable
                    // without scrolling past the findings cards
                    StickyCtaBar {
                        FunnelCta(text = FunnelCopy.RESULTS_CTA, onClick = onClaim)
                    }
                } else {
                    FunnelCta(
                        text = FunnelCopy.RESULTS_CTA,
                        onClick = onClaim,
                        modifier = Modifier.margin(top = 16.rpx).alignSelf(com.varabyte.kobweb.compose.css.AlignSelf.Center)
                    )
                }
            }
        }
    }
}
